import {YascEntityMapperV3} from '@yasc/yasc-material-components';
import {TrackerType} from '../../../enums';
import {
    ProjectCreateReq,
    ProjectTeamMemberReq,
    ProjectUpdateReq,
} from './project.contract';
import {ProjectMinRes, ProjectTrackerRes} from './tracker/project-tracker';
import {Project, ProjectInvoiceItem, ProjectTaskItem, ProjectTeamMember} from './project.model';
import {ApiDateAdapter} from '../../../utils/api-date-adapter';

export class ProjectMapper implements YascEntityMapperV3<ProjectMinRes, ProjectTrackerRes, Project, ProjectCreateReq, ProjectUpdateReq, number> {
    readonly modelTypes = {
        row: ProjectMinRes,
        detail: ProjectTrackerRes,
        domain: Project,
        create: ProjectCreateReq,
        patch: ProjectUpdateReq,
    };

    readonly idKey = 'id' as const;

    fromMinResponse(response: ProjectMinRes): Project {
        return new Project({
            id: response.id,
            name: response.name,
            internalName: response.internalName,
            workflowId: response.workflowId,
            currentWorkflowStatus: response.currentWorkflowStatus,
            currentStatusId: response.currentWorkflowStatus?.id ?? null,
            customer: response.customer,
            customerId: response.customer?.id ?? null,
            orderNr: response.orderNr,
            offerNr: response.offerNr,
            comment: response.comment,
            color: response.color,
            nextBill: this.toDateInputValue(response.nextBill),
            projectVolumeBrutto: response.projectVolumeBrutto ?? 0,
            projectVolumeBruttoOpen: response.projectVolumeBruttoOpen ?? 0,
            start: this.toDateInputValue(response.start),
            end: this.toDateInputValue(response.end),
            requireTasks: response.requireTasks ?? false,
            isActive: response.isActive ?? true,
            teamMemberCount: response.teamMemberCount ?? 0,
        });
    }

    fromDetailResponse(response: ProjectTrackerRes): Project {
        const project = this.fromMinResponse(response as unknown as ProjectMinRes);
        return project.clone({
            teamMembers: (response.teamMembers ?? []).map(member => new ProjectTeamMember({
                id: member.id,
                userId: member.user?.id ?? null,
                roleId: member.role,
                user: member.user,
                userName: member.user?.userName,
            })),
            tasks: (response.tasks ?? []).map(task => new ProjectTaskItem(task)),
            invoices: (response.invoices ?? []).map(invoice => new ProjectInvoiceItem(invoice)),
            teamMemberCount: response.teamMembers?.length ?? project.teamMemberCount,
        });
    }

    toCreateRequest(project: Project): ProjectCreateReq {
        return {
            name: project.name,
            internalName: project.internalName || undefined,
            type: TrackerType.Project,
            workflowId: this.requireNumber(project.workflowId, 'workflowId'),
            currentStatusId: this.requireNumber(project.currentStatusId, 'currentStatusId'),
            customerId: this.requireNumber(project.customerId, 'customerId'),
            orderNr: project.orderNr || undefined,
            offerNr: project.offerNr || undefined,
            comment: project.comment || undefined,
            color: project.color || undefined,
            nextBill: project.nextBill as any,
            projectVolumeBrutto: this.normalizeNumber(project.projectVolumeBrutto),
            requireTasks: project.requireTasks ?? false,
            isActive: true,
            start: this.toDateOnlyRequestValue(project.start),
            end: this.toDateOnlyRequestValue(project.end),
            teamMembers: project.teamMembers.map(member => this.toTeamMemberReq(member)),
            invoices: [],
            tasks: [],
            projectVolumeBruttoOpen: project.projectVolumeBruttoOpen ?? 0,
        } as ProjectCreateReq;
    }

    toPatchRequest(project: Project, original: Project | null, changedPaths: string[]): ProjectUpdateReq {
        const changed = new Set(changedPaths ?? []);
        const hasChanged = (path: string): boolean => !original || changed.has(path) || [...changed].some(x => x.startsWith(`${path}.`));

        const patch: Partial<ProjectUpdateReq> & {id: number} = {
            id: project.id,
        };

        if (hasChanged('name')) patch.name = project.name;
        if (hasChanged('internalName')) patch.internalName = project.internalName || undefined;
        if (hasChanged('orderNr')) patch.orderNr = project.orderNr || undefined;
        if (hasChanged('offerNr')) patch.offerNr = project.offerNr || undefined;
        if (hasChanged('comment')) patch.comment = project.comment ?? '';
        if (hasChanged('color')) patch.color = project.color || undefined;
        if (hasChanged('projectVolumeBrutto')) patch.projectVolumeBrutto = this.normalizeNumber(project.projectVolumeBrutto);
        if (hasChanged('customerId')) patch.customerId = this.requireNumber(project.customerId, 'customerId');
        if (hasChanged('currentStatusId')) patch.currentStatusId = this.requireNumber(project.currentStatusId, 'currentStatusId');
        if (hasChanged('nextBill')) patch.nextBill = project.nextBill as any;
        if (hasChanged('isActive')) patch.isActive = project.isActive;
        if (hasChanged('workflowId')) patch.workflowId = this.requireNumber(project.workflowId, 'workflowId');
        if (hasChanged('start')) patch.start = this.toDateOnlyRequestValue(project.start);
        if (hasChanged('end')) patch.end = this.toDateOnlyRequestValue(project.end);
        if (hasChanged('requireTasks')) patch.requireTasks = project.requireTasks;
        if (hasChanged('teamMembers')) patch.teamMembers = project.teamMembers.map(member => this.toTeamMemberReq(member));

        return patch as ProjectUpdateReq;
    }

    createEmptyDomain(): Project {
        return new Project({
            isActive: true,
            requireTasks: false,
            projectVolumeBrutto: 0,
            projectVolumeBruttoOpen: 0,
            teamMembers: [],
            tasks: [],
            invoices: [],
        });
    }

    getKey(project: Project): number {
        return project.id;
    }

    private toTeamMemberReq(member: ProjectTeamMember): ProjectTeamMemberReq {
        return new ProjectTeamMemberReq({
            userId: this.requireNumber(member.userId, 'userId'),
            roleId: this.requireNumber(member.roleId, 'roleId') as any,
        });
    }

    private resolveEntityId(value: unknown): number | null {
        if (value === undefined || value === null || value === '') return null;
        if (typeof value === 'number') return value;
        if (typeof value === 'string') {
            const parsed = Number(value);
            return Number.isNaN(parsed) ? null : parsed;
        }

        const entity = value as {id?: unknown; value?: unknown; key?: unknown};
        return this.resolveEntityId(entity.id ?? entity.value ?? entity.key ?? null);
    }

    private requireNumber(value: unknown, fieldName: string): number {
        const parsed = this.resolveEntityId(value);
        if (parsed === null || parsed <= 0) {
            throw new Error(`${fieldName} is required`);
        }

        return parsed;
    }

    private normalizeNumber(value: unknown): number {
        if (typeof value === 'number') return value;
        if (typeof value === 'string') {
            const parsed = Number(value.replace(',', '.'));
            return Number.isNaN(parsed) ? 0 : parsed;
        }

        return 0;
    }

    private toDateInputValue(value: unknown): string | undefined {
        if (!value) return undefined;
        if (typeof value === 'string') return value.slice(0, 10);
        if (value instanceof Date) return value.toISOString().slice(0, 10);

        const raw = value as {format?: (format: string) => string; toISOString?: () => string};
        if (typeof raw.format === 'function') return raw.format('YYYY-MM-DD');
        if (typeof raw.toISOString === 'function') return raw.toISOString().slice(0, 10);

        return undefined;
    }

    private toDateOnlyRequestValue(value: unknown): string | undefined {
        if (!value) return undefined;

        if (typeof value === 'string') {
            const normalized = this.toDateInputValue(value);
            return normalized ? ApiDateAdapter.toDateOnlyKey(normalized) : undefined;
        }

        if (value instanceof Date) return ApiDateAdapter.toDateOnlyKey(value);

        const raw = value as {format?: (format: string) => string; toDate?: () => Date; toISOString?: () => string};
        if (typeof raw.format === 'function') return raw.format('YYYY-MM-DD');
        if (typeof raw.toDate === 'function') return ApiDateAdapter.toDateOnlyKey(raw.toDate());
        if (typeof raw.toISOString === 'function') return ApiDateAdapter.toDateOnlyKey(raw.toISOString());

        return undefined;
    }
}
